<template>
  <InfoLayout
    v-if="!isMobile"
    :current-path="reverseFull({ name: 'socials' })"
    :is-policy="false"
  >
    <template #title>
      {{ t('Соцсети', options) }}
    </template>
    <template #content>
      <main>
        <section>
          <div :class="isMobile ? 'px-2 py-6' : 'column-count-3 ga-4 mb-n4'">
            <div
              v-for="(social, index) in socials"
              :key="index"
              class=""
              :class="isMobile ? '' : 'break-inside-avoid rounded elevation-1 pa-4 mb-4'"
            >
              <v-divider
                v-if="isMobile && index !== 0"
                class="my-6"
              />
              <div
                class="d-flex align-center gc-4"
                :class="isMobile ? 'mb-4' : 'mb-2'"
              >
                <div class="border-sm rounded-circle pa-1">
                  <v-img
                    :alt="social.title"
                    :src="social.logo"
                    width="48"
                    height="48"
                    contain
                  />
                </div>
                <div>
                  <div class="text-subtitle-1 text--text mb-1">
                    {{ social.title }}
                  </div>
                  <div
                    class="d-flex align-start"
                    :class="isMobile ? 'gc-4' : 'flex-column'"
                  >
                    <div
                      v-for="link in social.links"
                      :key="link.text"
                      class="d-inline-flex align-center gc-2"
                    >
                      <v-img
                        v-if="link.logo"
                        :src="link.logo"
                        width="16"
                        height="16"
                        contain
                      />
                      <span
                        v-else-if="link.icon"
                        class="ui-icon-fz-smaller"
                        :class="[link.icon, link.iconClasses]"
                      />
                      <button
                        type="button"
                        class="text-link-2 primary--text link-decoration-underline"
                        @click="handleClickLink(link.href)"
                      >
                        {{ link.text }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div :class="isMobile ? 'text-body-1 text--text' : 'text-body-2 text-secondary--text'">
                {{ social.description }}
              </div>
            </div>
          </div>
        </section>
      </main>
    </template>
  </InfoLayout>
  <main v-else>
    <section>
      <div :class="isMobile ? 'px-2 py-6' : 'column-count-3 ga-4 mb-n4'">
        <div
          v-for="(social, index) in socials"
          :key="index"
          class=""
          :class="isMobile ? '' : 'break-inside-avoid rounded elevation-1 pa-4 mb-4'"
        >
          <v-divider
            v-if="isMobile && index !== 0"
            class="my-6"
          />
          <div
            class="d-flex align-center gc-4"
            :class="isMobile ? 'mb-4' : 'mb-2'"
          >
            <div class="border-sm rounded-circle pa-1">
              <v-img
                :alt="social.title"
                :src="social.logo"
                width="48"
                height="48"
                contain
              />
            </div>
            <div>
              <div class="text-subtitle-1 text--text mb-1">
                {{ social.title }}
              </div>
              <div
                class="d-flex align-start"
                :class="isMobile ? 'gc-4' : 'flex-column'"
              >
                <div
                  v-for="link in social.links"
                  :key="link.text"
                  class="d-inline-flex align-center gc-2"
                >
                  <v-img
                    v-if="link.logo"
                    :src="link.logo"
                    width="16"
                    height="16"
                    contain
                  />
                  <span
                    v-else-if="link.icon"
                    class="ui-icon-fz-smaller"
                    :class="[link.icon, link.iconClasses]"
                  />
                  <button
                    type="button"
                    class="text-link-2 primary--text link-decoration-underline"
                    @click="handleClickLink(link.href)"
                  >
                    {{ link.text }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div :class="isMobile ? 'text-body-1 text--text' : 'text-body-2 text-secondary--text'">
            {{ social.description }}
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import I18NextVue from 'i18next'
import { useContextStore, useURLStore } from 'src/store'
import InfoLayout from 'src/layouts/info-pages/InfoLayout.vue'
import type { ContextStore } from 'src/components/pages/info/socials/types'

const { t, language } = I18NextVue
const options = { ns: language }

const { context }: ContextStore = useContextStore()
const { reverseFull } = useURLStore()

const isMobile = computed(() => context?.flavour === 'mobile')

const socials: Array<{
  logo: string
  title: string
  links: Array<{
    href: string
    text: string
    logo?: string
    icon?: string
    iconClasses?: string
  }>
  description: string
}> = [
  {
    logo: '/static/_v1/pd/logos/ui-kit/prodoctorov-logo.svg',
    title: 'ПроДокторов',
    links: [
      {
        href: 'https://vk.com/public120057026',
        text: 'ВКонтакте',
        icon: 'ui-icon-vk',
        iconClasses: 'brand--text',
      },
    ],
    description: 'ВКонтакте — это группа, где вы найдете медицинские новости, полезную информацию о здоровье, советы по уходу за собой и многое другое.',
  },
  {
    logo: '/static/_v1/pd/logos/ui-kit/medrocket-logo-icon.svg',
    title: 'Канал МедРокет',
    links: [
      {
        href: 'https://rutube.ru/channel/24959079/videos/',
        text: 'Rutube',
        logo: '/static/_v1/pd/icons/socials/rutube.png',
      },
      {
        href: 'https://vkvideo.ru/@medrocket',
        text: 'VК Video',
        logo: '/static/_v1/pd/icons/socials/vk-video.png',
      },
    ],
    description: 'Канал, где вы найдете полезные обучающие ролики для врачей, студентов и медицинских маркетологов.',
  },
  {
    logo: '/static/_v1/pd/logos/ui-kit/medrocket-logo-icon.svg',
    title: 'Телеграм-канал МедРокет',
    links: [
      {
        href: 'https://t.me/medroket',
        text: 'Телеграм',
        icon: 'ui-icon-logo-telegram-2',
        iconClasses: 'text-icon-telegram rounded-circle bg-gray-0--text button-text-snackbars',
      },
    ],
    description: 'Канал для врачей, медицинских маркетологов и руководителей клиник. Читайте анонсы медицинских мероприятий, а также новости и обновления от МедРокет.',
  },
  {
    logo: '/static/_v1/pd/logos/ui-kit/medtochka-logo-icon.svg',
    title: 'Сообщество МедТочки',
    links: [
      {
        href: 'https://vk.com/medtochkaa',
        text: 'ВКонтакте',
        icon: 'ui-icon-vk',
        iconClasses: 'brand--text',
      },
    ],
    description: 'Полезная информация о\xA0здоровье, советы от\xA0врачей и\xA0блогеров, описание возможностей приложения МедТочка.',
  },
  {
    logo: '/static/_v1/pd/logos/ui-kit/prodoctorov-logo-icon.svg',
    title: 'Телеграм-канал ПроДокторов',
    links: [
      {
        href: 'https://t.me/prodoctorovru',
        text: 'Телеграм',
        icon: 'ui-icon-logo-telegram-2',
        iconClasses: 'text-icon-telegram rounded-circle bg-gray-0--text button-text-snackbars',
      },
    ],
    description: 'Канал для пользователей портала ПроДокторов, которым небезразлична медицина будущего в России.',
  },
]

function handleClickLink(link: string) {
  window.open(link, '_blank')
}
</script>

<style lang="scss" scoped>
@import 'src/styles/index';

.text-icon-telegram {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 1em;
  width: 1em;
  line-height: 1em;
  padding: 0 0.125em 0.125em;

  &::before {
    font-size: 0.75em;
  }
}
</style>
